import { DASHBOARD_TARGETS } from "../spotlight/targets";
import { DASHBOARD_TOUR_STEP_ORDER, type DashboardTourStepId } from "./schema";
import type {
  DashboardTourContextData,
  DashboardTourStepDefinition,
} from "./types";

function formatLocaleSummary(ctx: DashboardTourContextData): string {
  if (ctx.localesEnabled.length === 0) {
    return "No active locales detected yet.";
  }

  return ctx.localesEnabled.map((locale) => locale.toUpperCase()).join(", ");
}

function routeLinkBody(label: string, detail: string) {
  return `${detail} Use the highlighted ${label} link in the sidebar, or the button on this card.`;
}

export const DASHBOARD_TOUR_STEPS: DashboardTourStepDefinition[] = [
  {
    id: "welcome",
    tier: "hard",
    target: null,
    placement: "modal-center",
    copy: (ctx) => ({
      title: "Take a guided dashboard tour",
      body: ctx.hasActiveSite
        ? "I’ll walk you through the customer dashboard in the order a workspace typically uses it: context, content, launch operations, multilingual tools, and advanced SEO."
        : "I’ll start with the workspace controls so you can create the first site, then the rest of the dashboard can open up in the right order once that site exists.",
      primaryLabel: "Start tour",
      secondaryLabel: "Skip for now",
      hint: ctx.hasActiveSite
        ? "You can move with the highlighted sidebar links or use the card buttons as a fallback."
        : "This version adapts to workspaces that do not have an active site yet.",
    }),
  },
  {
    id: "cockpit",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_COCKPIT,
    placement: "right-of-target",
    copy: (ctx) => ({
      title: "Start with the workspace cockpit",
      body: "This cockpit controls the active workspace and site. If the rest of the dashboard ever looks unfamiliar, check this area first because every page follows the context selected here.",
      primaryLabel: "Continue",
      hint: ctx.canManageWorkspace
        ? "Admins can switch workspaces, change the active site, and create another workspace here."
        : "You can still confirm which workspace and site you are currently editing from here.",
    }),
  },
  {
    id: "overview_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_OVERVIEW,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/",
    copy: () => ({
      title: "Open Overview next",
      body: routeLinkBody(
        "Overview",
        "Overview is the fastest operating snapshot for the active site.",
      ),
      primaryLabel: "Open Overview",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/");
    },
  },
  {
    id: "overview_page",
    tier: "soft",
    target: (ctx) =>
      ctx.onboarding && !ctx.onboarding.completedAt
        ? DASHBOARD_TARGETS.OVERVIEW_CHECKLIST
        : DASHBOARD_TARGETS.OVERVIEW_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: (ctx) => ({
      title: "Overview is your operating snapshot",
      body: "Use this page to see launch progress, recent page activity, inquiry status, domain state, deployments, and other high-signal site health indicators at a glance.",
      primaryLabel: "Continue",
      hint:
        ctx.onboarding && !ctx.onboarding.completedAt
          ? `Current launch checklist progress: ${ctx.onboarding.progress.completed}/${ctx.onboarding.progress.total}.`
          : "When you need a fast answer about what needs attention, start here.",
    }),
  },
  {
    id: "workspace_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_WORKSPACE,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.canManageWorkspace,
    autoPass: (ctx) => ctx.pathname === "/workspace",
    copy: () => ({
      title: "Open Workspace Studio",
      body: routeLinkBody(
        "Workspace Studio",
        "Workspace Studio is the admin control room for sites, teammates, and workspace context.",
      ),
      primaryLabel: "Open Workspace Studio",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/workspace");
    },
  },
  {
    id: "workspace_page",
    tier: "soft",
    target: (ctx) =>
      ctx.hasActiveSite
        ? DASHBOARD_TARGETS.WORKSPACE_HERO
        : DASHBOARD_TARGETS.WORKSPACE_NEW_SITE,
    placement: "below-target",
    isRelevant: (ctx) => ctx.canManageWorkspace,
    copy: (ctx) =>
      ctx.hasActiveSite
        ? {
            title: "Workspace Studio manages sites and teammates",
            body: "Use this page to provision another site, switch active site context, invite teammates, and keep workspace-level operations organized without leaving the tenant.",
            primaryLabel: "Continue",
            hint: "This page matters most when you manage more than one site or collaborate with a team.",
          }
        : {
            title: "Create the first site here",
            body: "This workspace does not have an active site yet. Create it from this page first, then the content, deployment, domains, and settings areas of the dashboard will light up automatically.",
            primaryLabel: "Continue after the site exists",
            secondaryLabel: "Skip walkthrough",
            hint: "When the first site becomes active, the tour can resume from Pages.",
          },
    primaryAction: (ctx) => {
      if (!ctx.hasActiveSite) {
        ctx.pauseAt("site_required", "pages_nav");
        return;
      }
      ctx.advance();
    },
    secondaryAction: (ctx) => {
      if (!ctx.hasActiveSite) {
        ctx.skipTour();
        return;
      }
      ctx.advance();
    },
  },
  {
    id: "pages_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_PAGES,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/pages",
    copy: () => ({
      title: "Open Pages",
      body: routeLinkBody(
        "Pages",
        "Pages is the content library for the active site, including each locale variant once multilingual publishing is enabled.",
      ),
      primaryLabel: "Open Pages",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/pages");
    },
  },
  {
    id: "pages_page",
    tier: "soft",
    target: (ctx) =>
      ctx.milestoneDone("FIRST_PAGE_PUBLISHED")
        ? DASHBOARD_TARGETS.PAGES_HEADER
        : DASHBOARD_TARGETS.PAGES_NEW_PAGE,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: (ctx) =>
      ctx.milestoneDone("FIRST_PAGE_PUBLISHED")
        ? {
            title: "Pages is your content inventory",
            body: "Create new pages, open drafts, manage published routes, and work locale by locale from this library.",
            primaryLabel: "Continue",
            hint: "Use New Page whenever you want to add another route to the site.",
          }
        : {
            title: "Start the first page from here",
            body: "This is where every page for the active site lives. If you want, I can hand you off to the page creator now and the editor walkthrough will take over from there.",
            primaryLabel: "Create first page",
            secondaryLabel: "Continue dashboard tour",
            hint: "If you skip this for now, come back here and use New Page when you are ready.",
          },
    primaryAction: (ctx) => {
      if (!ctx.milestoneDone("FIRST_PAGE_PUBLISHED")) {
        const next = ctx.getNextStepId("pages_page") ?? "complete";
        ctx.pauseAt("editor_handoff", next);
        ctx.navigate("/pages/new");
        return;
      }
      ctx.advance();
    },
  },
  {
    id: "settings_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_SETTINGS,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.canOpenSettings,
    autoPass: (ctx) => ctx.pathname === "/settings",
    copy: () => ({
      title: "Open Settings",
      body: routeLinkBody(
        "Settings",
        "Settings holds the site-wide rules for localization, default SEO metadata, and go-live readiness.",
      ),
      primaryLabel: "Open Settings",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/settings");
    },
  },
  {
    id: "settings_page",
    tier: "soft",
    target: (ctx) =>
      ctx.localesEnabled.length <= 1
        ? DASHBOARD_TARGETS.SETTINGS_TAB_LOCALIZATION
        : DASHBOARD_TARGETS.SETTINGS_TAB_SEO,
    placement: "below-target",
    isRelevant: (ctx) => ctx.canOpenSettings,
    copy: (ctx) => ({
      title:
        ctx.localesEnabled.length <= 1
          ? "Enable more locales and set site-wide SEO defaults"
          : "Settings controls site-wide publishing rules",
      body:
        ctx.localesEnabled.length <= 1
          ? "Use Localization to enable additional publishing languages. SEO Defaults sets the shared title template, default description, and share image used across the site."
          : "Use SEO Defaults for shared metadata, Localization for language coverage, and Go-Live Readiness to review what still blocks launch.",
      primaryLabel: "Continue",
      hint: `Active locales: ${formatLocaleSummary(ctx)}.`,
    }),
  },
  {
    id: "forms_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_FORMS,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/forms",
    copy: () => ({
      title: "Open Inquiries",
      body: routeLinkBody(
        "Inquiries",
        "Inquiries combines the guest inbox, reusable forms, and branded email delivery in one place.",
      ),
      primaryLabel: "Open Inquiries",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/forms");
    },
  },
  {
    id: "forms_page",
    tier: "soft",
    target: DASHBOARD_TARGETS.FORMS_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: () => ({
      title: "Inquiries covers inbox, forms, and email branding",
      body: "Use Inbox to review submissions, Forms to build reusable inquiry flows, and Emails to brand the notifications and confirmations guests or staff receive.",
      primaryLabel: "Continue",
      hint: "When your contact path changes, this is the page that controls it.",
    }),
  },
  {
    id: "deployments_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_DEPLOYMENTS,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/deployments",
    copy: () => ({
      title: "Open Deployments",
      body: routeLinkBody(
        "Deployments",
        "Deployments is where you publish live changes and inspect rollout state for the active site.",
      ),
      primaryLabel: "Open Deployments",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/deployments");
    },
  },
  {
    id: "deployments_page",
    tier: "soft",
    target: (ctx) =>
      ctx.canManageWorkspace
        ? DASHBOARD_TARGETS.DEPLOYMENTS_PUBLISH
        : DASHBOARD_TARGETS.DEPLOYMENTS_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: (ctx) => ({
      title: ctx.milestoneDone("DEPLOYMENT_PROVISIONED")
        ? "Deployments ships later updates"
        : "Deployments takes the site live",
      body: ctx.milestoneDone("DEPLOYMENT_PROVISIONED")
        ? "This workspace already has deployment history. Return here whenever you want to publish later changes, inspect rollout status, retry failed runs, or roll back."
        : "When the site is ready for the web, publish it from here. StayLayer provisions and updates the hosted runtime from this page.",
      primaryLabel: "Continue",
      hint: ctx.canManageWorkspace
        ? "Owners and admins can publish from this page."
        : "You can inspect status here, but an admin must publish live changes.",
    }),
  },
  {
    id: "domains_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_DOMAINS,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/domains",
    copy: () => ({
      title: "Open Domains",
      body: routeLinkBody(
        "Domains",
        "Domains is where you connect a real hostname, verify DNS, and control the canonical public address for the site.",
      ),
      primaryLabel: "Open Domains",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/domains");
    },
  },
  {
    id: "domains_page",
    tier: "soft",
    target: (ctx) =>
      ctx.canManageWorkspace
        ? DASHBOARD_TARGETS.DOMAINS_CONNECT
        : DASHBOARD_TARGETS.DOMAINS_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: (ctx) => ({
      title: ctx.milestoneDone("DOMAIN_CONNECTED")
        ? "Domains manages the public hostname"
        : "Connect the public domain from here",
      body: ctx.milestoneDone("DOMAIN_CONNECTED")
        ? "This site already has domain routing in place. Come back here whenever you need to verify DNS, adjust the canonical host, or connect another domain."
        : "Use this page to connect a custom domain, verify DNS, and make sure guests land on the right hostname once the site is live.",
      primaryLabel: "Continue",
      hint: ctx.canManageWorkspace
        ? "The platform can inspect DNS and explain issues, but registrar changes still happen at your DNS provider."
        : "You can inspect domain status here even if you cannot change it.",
    }),
  },
  {
    id: "usage_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_USAGE,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasContentAccess,
    autoPass: (ctx) => ctx.pathname === "/usage",
    copy: () => ({
      title: "Open Usage",
      body: routeLinkBody(
        "Usage",
        "Usage combines plan consumption with the launch checklist, so it is the best place to see what is finished and what still blocks launch.",
      ),
      primaryLabel: "Open Usage",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/usage");
    },
  },
  {
    id: "usage_page",
    tier: "soft",
    target: (ctx) =>
      ctx.onboarding
        ? DASHBOARD_TARGETS.USAGE_CHECKLIST
        : DASHBOARD_TARGETS.USAGE_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasContentAccess,
    copy: (ctx) => ({
      title: "Usage is the launch-control page",
      body: "This page combines usage limits with the launch checklist, so you can track what is done, what is still blocked, and where to go next.",
      primaryLabel: "Continue",
      hint: ctx.onboarding
        ? `Checklist progress: ${ctx.onboarding.progress.completed}/${ctx.onboarding.progress.total}.`
        : "Return here any time you want the fastest cross-functional progress view.",
    }),
  },
  {
    id: "translation_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_TRANSLATION,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/translation",
    copy: () => ({
      title: "Open Translation",
      body: routeLinkBody(
        "Translation",
        "Translation is where you create multilingual publishing jobs and review translation status for the active site.",
      ),
      primaryLabel: "Open Translation",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/translation");
    },
  },
  {
    id: "translation_page",
    tier: "soft",
    target: DASHBOARD_TARGETS.TRANSLATION_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: (ctx) => ({
      title:
        ctx.localesEnabled.length <= 1
          ? "Translation starts after you enable another locale"
          : "Translation jobs run from here",
      body:
        ctx.localesEnabled.length <= 1
          ? "Once another language is active in Settings, create translation jobs here and review multilingual completeness without leaving the dashboard."
          : "Use this page to translate page sets, review job status, and approve multilingual content after it is generated.",
      primaryLabel: "Continue",
      hint:
        ctx.localesEnabled.length <= 1
          ? "Glossary is the next stop if you want brand terms to stay consistent before translation begins."
          : `Current locales: ${formatLocaleSummary(ctx)}.`,
    }),
  },
  {
    id: "glossary_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_GLOSSARY,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/glossary",
    copy: () => ({
      title: "Open Glossary",
      body: routeLinkBody(
        "Glossary",
        "Glossary keeps property names, room types, and hospitality phrases consistent before or after translation.",
      ),
      primaryLabel: "Open Glossary",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/glossary");
    },
  },
  {
    id: "glossary_page",
    tier: "soft",
    target: DASHBOARD_TARGETS.GLOSSARY_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: () => ({
      title: "Glossary locks key terms before translation",
      body: "Store brand phrases, property names, and room types here so translated pages remain consistent instead of drifting from your preferred language.",
      primaryLabel: "Continue",
      hint: "Translation and Glossary work best together, but you can build the glossary at any time.",
    }),
  },
  {
    id: "seo_nav",
    tier: "soft",
    target: DASHBOARD_TARGETS.SIDEBAR_LINK_SEO,
    placement: "right-of-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    autoPass: (ctx) => ctx.pathname === "/seo",
    copy: () => ({
      title: "Open SEO",
      body: routeLinkBody(
        "SEO",
        "SEO is the advanced growth workspace for audits, redirects, structured data, search integrations, and performance checks.",
      ),
      primaryLabel: "Open SEO",
    }),
    primaryAction: (ctx) => {
      ctx.navigate("/seo");
    },
  },
  {
    id: "seo_page",
    tier: "soft",
    target: DASHBOARD_TARGETS.SEO_HEADER,
    placement: "below-target",
    isRelevant: (ctx) => ctx.hasActiveSite,
    copy: () => ({
      title: "SEO is the advanced optimization toolkit",
      body: "Use this page for audits, redirects, structured data, search-console integrations, crawl checks, performance work, and other deeper SEO operations once the site is live.",
      primaryLabel: "Continue",
      hint: "Site-wide metadata still lives in Settings, and page-level SEO lives in the editor.",
    }),
  },
  {
    id: "complete",
    tier: "soft",
    showDefaultSecondary: false,
    target: null,
    placement: "modal-center",
    copy: () => ({
      title: "You know the dashboard now",
      body: "You’ve seen the main places for workspace setup, content, launch operations, multilingual growth, and advanced SEO. From here, the fastest next move is to keep shipping pages and come back to Usage whenever you need a launch-status reset.",
      primaryLabel: "Finish",
    }),
    primaryAction: (ctx) => {
      ctx.completeTour();
    },
  },
];

export const DASHBOARD_TOUR_STEP_BY_ID: Record<
  DashboardTourStepId,
  DashboardTourStepDefinition
> = DASHBOARD_TOUR_STEPS.reduce(
  (acc, step) => {
    acc[step.id] = step;
    return acc;
  },
  {} as Record<DashboardTourStepId, DashboardTourStepDefinition>,
);

export function nextDashboardTourStepId(
  current: DashboardTourStepId,
): DashboardTourStepId | null {
  const index = DASHBOARD_TOUR_STEP_ORDER.indexOf(current);
  if (index < 0) return null;
  return DASHBOARD_TOUR_STEP_ORDER[index + 1] ?? null;
}
